<script setup lang="ts">
/**
 * ResidentLayout — simpler chrome for /resident/* routes.
 * Mirrors resident-dashboard.html .rb-top header exactly.
 * No sidebar. HOA name + brand mark + resident nav + user menu.
 */
import { ref } from 'vue';
import { RouterLink, RouterView } from 'vue-router';
import { useAuth } from '@/stores/auth';

const auth = useAuth();
const userMenuOpen = ref(false);

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value;
}

function closeUserMenu() {
  userMenuOpen.value = false;
}

async function onSignOut() {
  userMenuOpen.value = false;
  await auth.logout();
}

// Compute display name / initials from the user claims.
const userName = () => auth.user?.displayName ?? 'Resident';
const userEmail = () => auth.user?.email ?? '';
const initials = () => {
  const n = userName();
  const parts = n.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0] ?? '') + (parts[1]![0] ?? '');
  return n.slice(0, 2).toUpperCase();
};
const firstNameOnly = () => userName().split(/\s+/)[0] ?? 'Resident';
</script>

<template>
  <div class="min-h-screen" style="background: linear-gradient(180deg, var(--navy-50), var(--bg) 18%);">
    <!-- Topbar — mirrors .rb-top in resident-dashboard.html -->
    <header class="rb-top">
      <div class="rb-top-inner">
        <!-- Brand -->
        <RouterLink to="/resident" class="rb-brand">
          <div class="brand-mark">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 11.5L12 4l9 7.5" />
              <path d="M5 10v10h14V10" />
              <circle cx="12" cy="14" r="2.2" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <div>
            <div class="nm">HOA Watch</div>
            <div class="sub">Resident portal</div>
          </div>
        </RouterLink>

        <!-- Nav links (hidden ≤720px per prototype) -->
        <nav class="rb-nav" aria-label="Resident navigation">
          <RouterLink to="/resident" class="rb-nav-item" active-class="active">My HOA</RouterLink>
          <RouterLink to="/portal" class="rb-nav-item">Report issue</RouterLink>
        </nav>

        <!-- Actions -->
        <div class="rb-actions">
          <!-- User menu -->
          <div
            class="rb-user"
            role="button"
            tabindex="0"
            :aria-expanded="userMenuOpen"
            aria-haspopup="true"
            data-testid="resident-user-btn"
            @click="toggleUserMenu"
            @keydown.enter="toggleUserMenu"
            @keydown.escape="closeUserMenu"
          >
            <div class="av">{{ initials() }}</div>
            <div class="nm-small">{{ firstNameOnly() }}</div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:12px;height:12px;color:var(--muted);">
              <path d="M6 9l6 6 6-6" />
            </svg>

            <!-- Dropdown menu -->
            <div
              class="rb-user-menu"
              :class="{ open: userMenuOpen }"
              role="menu"
              data-testid="resident-user-menu"
              @click.stop
            >
              <div class="menu-head">
                <div class="name">{{ userName() }}</div>
                <div class="email font-mono tabular-nums">{{ userEmail() }}</div>
              </div>
              <a href="#" role="menuitem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Account settings
              </a>
              <a href="#" role="menuitem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                </svg>
                Notification preferences
              </a>
              <div class="sep" />
              <RouterLink to="/portal" role="menuitem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="10" rx="2" />
                  <path d="M14 9V5a3 3 0 0 0-6 0v4" />
                </svg>
                Public portal
              </RouterLink>
              <div class="sep" />
              <button
                class="signout"
                role="menuitem"
                data-testid="resident-signout-btn"
                @click="onSignOut"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <path d="m16 17 5-5-5-5" />
                  <path d="M21 12H9" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Overlay to close menu on outside click -->
    <div
      v-if="userMenuOpen"
      class="fixed inset-0 z-[25]"
      aria-hidden="true"
      @click="closeUserMenu"
    />

    <main>
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
/* Resident topbar — mirrors resident-dashboard.html */
.rb-top {
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 30;
}
.rb-top-inner {
  max-width: 980px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.rb-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
}
.brand-mark {
  width: 36px;
  height: 36px;
  background: var(--navy-800);
  border-radius: var(--r-md, 8px);
  display: grid;
  place-items: center;
  color: white;
  flex-shrink: 0;
}
.brand-mark svg { width: 18px; height: 18px; }
.rb-brand .nm {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.rb-brand .sub {
  font-size: 11.5px;
  color: var(--muted);
  margin-top: -1px;
}
.rb-nav {
  margin-left: 24px;
  display: flex;
  gap: 4px;
  align-items: center;
}
.rb-nav-item {
  padding: 7px 12px;
  border-radius: var(--r-md, 8px);
  font-size: 13.5px;
  font-weight: 500;
  color: var(--fg-soft);
  text-decoration: none;
}
.rb-nav-item:hover { background: var(--bg-soft); color: var(--fg); }
.rb-nav-item.active { background: var(--navy-100); color: var(--navy-800); font-weight: 600; }
@media (max-width: 720px) { .rb-nav { display: none; } }

.rb-actions {
  margin-left: auto;
  display: flex;
  gap: 10px;
  align-items: center;
}

/* User pill */
.rb-user {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: 999px;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  cursor: pointer;
  position: relative;
  user-select: none;
}
.rb-user:focus-visible { outline: 2px solid var(--navy-600); outline-offset: 2px; }
.rb-user .av {
  width: 28px;
  height: 28px;
  background: var(--navy-700);
  color: white;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.rb-user .nm-small { font-size: 13px; font-weight: 600; }
@media (max-width: 600px) {
  .rb-user .nm-small { display: none; }
  .rb-user { padding: 4px; }
}

/* Dropdown */
.rb-user-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--r-md, 8px);
  box-shadow: var(--shadow-lg);
  padding: 6px;
  min-width: 220px;
  display: none;
  z-index: 50;
}
.rb-user-menu.open { display: block; }
.rb-user-menu .menu-head {
  padding: 10px 10px 8px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 6px;
}
.rb-user-menu .menu-head .name { font-size: 13px; font-weight: 600; }
.rb-user-menu .menu-head .email { font-size: 11.5px; color: var(--muted); margin-top: 1px; }
.rb-user-menu a,
.rb-user-menu button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--r-sm, 4px);
  font-size: 13px;
  color: var(--fg-soft);
  text-decoration: none;
  font-weight: 500;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}
.rb-user-menu a:hover,
.rb-user-menu button:hover { background: var(--bg-soft); color: var(--fg); }
.rb-user-menu a svg,
.rb-user-menu button svg { width: 14px; height: 14px; color: var(--muted-2); flex-shrink: 0; }
.rb-user-menu .sep { height: 1px; background: var(--border); margin: 6px -6px; }
.rb-user-menu .signout { color: var(--red-700); }
.rb-user-menu .signout:hover { background: var(--red-50); color: var(--red-700); }
.rb-user-menu .signout svg { color: var(--red-600); }
</style>
